import { differenceInCalendarDays, format, parseISO } from 'date-fns';

export const formatDashboardCount = (value?: number | null): string =>
  typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString('en-US') : '0';

const DASHBOARD_CURRENCY_SYMBOL_OVERRIDES: Record<string, string> = {
  AUD: '$',
  CAD: '$',
  EGP: '£',
  HKD: '$',
  NZD: '$',
  SGD: '$',
  USD: '$',
};

export const formatDashboardCurrency = (
  value?: number | null,
  currency?: string | null
): string => {
  const amount = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  const formattedAmount = amount.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
  });
  const resolvedCurrency = currency?.trim().toUpperCase();

  if (!resolvedCurrency) {
    return formattedAmount;
  }

  const overrideSymbol = DASHBOARD_CURRENCY_SYMBOL_OVERRIDES[resolvedCurrency];

  if (overrideSymbol) {
    return `${overrideSymbol}${formattedAmount}`;
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: resolvedCurrency,
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
  }).format(amount);
};

export const parseDashboardDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const date = value.includes('T') ? parseISO(value) : parseISO(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDashboardTime = (value?: string | null): string => {
  const date = parseDashboardDate(value);
  return date ? format(date, 'h:mm a') : 'TBD';
};

export const formatDashboardDateLabel = (value?: string | null): string => {
  const date = parseDashboardDate(value);
  return date ? format(date, 'MMM d') : 'No date';
};

export const formatDashboardDayNumber = (value?: string | null): string => {
  const date = parseDashboardDate(value);
  return date ? format(date, 'd') : '--';
};

export const formatDashboardMonthShort = (value?: string | null): string => {
  const date = parseDashboardDate(value);
  return date ? format(date, 'MMM').toUpperCase() : 'TBD';
};

export const formatDashboardDateRange = (start?: string | null, end?: string | null): string => {
  const startDate = parseDashboardDate(start);
  const endDate = parseDashboardDate(end);
  if (startDate && endDate) return `${format(startDate, 'd MMM')} - ${format(endDate, 'd MMM')}`;
  if (startDate) return format(startDate, 'd MMM');
  if (endDate) return format(endDate, 'd MMM');
  return 'Dates TBD';
};

export const formatDashboardDueLabel = (dueDate?: string | null): string => {
  const date = parseDashboardDate(dueDate);
  if (!date) return 'Due date TBD';
  const overdueDays = differenceInCalendarDays(new Date(), date);
  if (overdueDays > 0) {
    return `Due ${format(date, 'MMM d')} · ${overdueDays} day${overdueDays === 1 ? '' : 's'} overdue`;
  }
  return `Due ${format(date, 'MMM d')}`;
};

export const formatDashboardPaidDate = (value?: string | null): string | null => {
  const date = parseDashboardDate(value);
  return date ? format(date, 'MMM d') : null;
};

export const toDashboardTitle = (value?: string | null): string => {
  const cleaned = String(value || '').replace(/[_-]+/g, ' ').trim();
  if (!cleaned) return '';
  return cleaned.replace(/\b\w/g, (letter) => letter.toUpperCase());
};
